/**
 * useReanalyzeStatus - Polling-based hook for re-analyze status tracking
 *
 * Provides real-time feedback while analysis is in progress:
 * - Polls record status every 2.5s
 * - Detects terminal states (analyzed, analysis_failed, completed, failed)
 * - Timeout after 2 minutes
 * - Callbacks for success/error/timeout
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { logError, logInfo, logWarning } from '@/lib/logger';
import { toAnalysisData, type AnalysisData } from '@/lib/api/mappers';
import { isProcessingPausedError } from '@/lib/api/processing-paused';
import {
  FAILED_RECORD_STATUSES,
  SUCCESSFUL_RECORD_STATUSES,
  isFailedRecordStatus,
  isSuccessfulRecordStatus,
} from '@/lib/records/status';

// Terminal states that indicate analysis is complete
const TERMINAL_STATES = [
  'analyzed',
  ...SUCCESSFUL_RECORD_STATUSES,
  ...FAILED_RECORD_STATUSES,
];

// States that indicate analysis is in progress
const ANALYZING_STATES = ['analyzing', 'transcribed'];

export type ReanalyzeStatus =
  'idle' | 'queued' | 'analyzing' | 'completed' | 'failed' | 'timeout';

export interface UseReanalyzeStatusOptions {
  /** Called when analysis completes successfully */
  onComplete?: (analysis: AnalysisData) => void;
  /** Called when analysis fails */
  onError?: (message: string) => void;
  /**
   * Called instead of `onError` when the server refuses the start because the
   * record's source has processing paused or withdrawn (#2709).
   */
  onProcessingPaused?: () => void;
  /** Called when polling times out */
  onTimeout?: () => void;
  /** Polling interval in ms (default: 2500) */
  pollIntervalMs?: number;
  /** Timeout in ms (default: 120000 = 2 minutes) */
  timeoutMs?: number;
}

export interface UseReanalyzeStatusReturn {
  /** Current status */
  status: ReanalyzeStatus;
  /** Error message if failed */
  error: string | null;
  /** Whether polling is active */
  isPolling: boolean;
  /** Start re-analyze and begin polling */
  startReanalyze: (
    recordId: string,
    promptProfileId?: string,
    expectedCurrentAnalysisId?: string | null,
    source?: { mode: 'from_asr_source' } | { mode: 'analysis_only'; transcriptId: string }
  ) => Promise<void>;
  /** Cancel polling */
  cancel: () => void;
  /** Elapsed time in seconds since reanalyze started */
  elapsedSeconds: number;
}

export function useReanalyzeStatus(
  options: UseReanalyzeStatusOptions = {},
  apiClientOverride?: ReturnType<typeof useAuth>['apiClient']
): UseReanalyzeStatusReturn {
  const {
    onComplete,
    onError,
    onProcessingPaused,
    onTimeout,
    pollIntervalMs = 2500,
    timeoutMs = 120000,
  } = options;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const apiClient = apiClientOverride ?? useAuth().apiClient;

  const [status, setStatus] = useState<ReanalyzeStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Use refs to avoid stale closures in intervals
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const recordIdRef = useRef<string | null>(null);
  const expectedCurrentAnalysisIdRef = useRef<string | null | undefined>(undefined);
  /**
   * The record row as the FIRST poll of this replay saw it. Replay preserves
   * a pre-existing terminal status (finalizeFailedRecordStatus, workflows-api
   * record-pipeline/utils/record-status.ts) — it declines to overwrite one and
   * does not touch the row — so a record that was already `analysis_failed`
   * before the click still reads `analysis_failed` while the new run is very
   * much alive. Comparing against this snapshot is what separates that
   * preserved status from a failure THIS replay just wrote, without comparing
   * a server timestamp to the browser's clock.
   */
  const replayBaselineRef = useRef<{ status: string; updatedAt: string | null } | null>(
    null
  );
  const isPollInFlightRef = useRef(false);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
    isPollInFlightRef.current = false;
    setIsPolling(false);
  }, []);

  // Cancel function
  const cancel = useCallback(() => {
    cleanup();
    setStatus('idle');
    setError(null);
    setElapsedSeconds(0);
    recordIdRef.current = null;
    expectedCurrentAnalysisIdRef.current = undefined;
    replayBaselineRef.current = null;
    logInfo('Reanalyze cancelled by user', { component: 'useReanalyzeStatus' });
  }, [cleanup]);

  // Poll for status
  const pollStatus = useCallback(async () => {
    const recordId = recordIdRef.current;
    if (!recordId || isPollInFlightRef.current) return;

    isPollInFlightRef.current = true;
    try {
      // Fetch record to get current status
      const response = await apiClient.records.getRecordById(recordId);
      const recordStatus = response.record.status;
      const recordUpdatedAt = response.record.updatedAt ?? null;

      // Established on the first poll of this replay and never again, so the
      // comparison below is always server-timestamp vs server-timestamp.
      replayBaselineRef.current ??= { status: recordStatus, updatedAt: recordUpdatedAt };
      const baseline = replayBaselineRef.current;

      logInfo('Polling record status', {
        component: 'useReanalyzeStatus',
        recordId,
        status: recordStatus,
      });

      // Check if analyzing
      if (ANALYZING_STATES.includes(recordStatus)) {
        setStatus('analyzing');
        return; // Continue polling
      }

      // Check for terminal states
      if (TERMINAL_STATES.includes(recordStatus)) {
        const hasPinnedVersion = expectedCurrentAnalysisIdRef.current !== undefined;
        const hasNewAnalysis =
          hasPinnedVersion &&
          !!response.record.analysisId &&
          response.record.analysisId !== expectedCurrentAnalysisIdRef.current;
        // A failure THIS replay produced, as opposed to the pre-replay status
        // the backend deliberately preserved: the row is in a failed status
        // AND it changed since the first poll of this run — either it moved
        // into the failed status, or it was rewritten (a fresh
        // `updateRecordStatus(..., 'failed', error)`) while already in one.
        // Without this the branch below was structurally unreachable: every
        // caller pins an analysis id, a failed replay promotes no new
        // analysis, so the poller reported 'queued' for the full 120s and
        // then a generic timeout — the record's own errorMessage never
        // reached the user. A failure the backend chose NOT to record
        // (preserving an existing terminal SUCCESS status) is still
        // invisible from here; only the timeout can cover that.
        const failedThisReplay =
          isFailedRecordStatus(recordStatus) &&
          (recordStatus !== baseline.status || recordUpdatedAt !== baseline.updatedAt);

        // The record may have been successful OR failed before replay. Its
        // operational delivery status is intentionally preserved, so the CAS
        // analysis ID — not a status transition — is the completion signal.
        if (hasPinnedVersion && !hasNewAnalysis && !failedThisReplay) {
          setStatus('queued');
          return;
        }

        if (
          recordStatus === 'analyzed' ||
          isSuccessfulRecordStatus(recordStatus) ||
          hasNewAnalysis
        ) {
          cleanup();
          // Fetch the new analysis
          if (response.record.analysisId) {
            try {
              const analysis = await apiClient.analyses.getAnalysisById(
                response.record.analysisId
              );
              if (analysis) {
                const transformedAnalysis = toAnalysisData(analysis);
                setStatus('completed');
                onComplete?.(transformedAnalysis);
                logInfo('Reanalyze completed successfully', {
                  component: 'useReanalyzeStatus',
                  recordId,
                  analysisId: response.record.analysisId,
                });
              }
            } catch (err) {
              logError(
                'Failed to fetch new analysis after reanalyze',
                { recordId, analysisId: response.record.analysisId },
                err instanceof Error ? err : undefined
              );
              setStatus('failed');
              setError('Analysis completed but failed to fetch results');
              onError?.('Analysis completed but failed to fetch results');
            }
          } else {
            // No analysisId but terminal state - this shouldn't happen after reanalyze
            setStatus('completed');
            logWarning('Reanalyze completed but no analysisId found', {
              component: 'useReanalyzeStatus',
              recordId,
              status: recordStatus,
            });
            // Don't call onComplete without a valid analysis
          }
        } else if (isFailedRecordStatus(recordStatus)) {
          // Failed state
          cleanup();
          setStatus('failed');
          const errorMsg = response.record.errorMessage || 'Analysis failed';
          setError(errorMsg);
          onError?.(errorMsg);
          logWarning('Reanalyze failed', {
            component: 'useReanalyzeStatus',
            recordId,
            status: recordStatus,
            error: errorMsg,
          });
        }
        return;
      }

      // Still in non-terminal state (pending, transcribing, etc.) - continue polling
      setStatus('queued');
    } catch (err) {
      // Network error - don't fail immediately, let it retry
      logWarning('Poll request failed, will retry', {
        component: 'useReanalyzeStatus',
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      isPollInFlightRef.current = false;
    }
  }, [apiClient, cleanup, onComplete, onError]);

  // Start re-analyze
  const startReanalyze = useCallback(
    async (
      recordId: string,
      promptProfileId?: string,
      expectedCurrentAnalysisId?: string | null,
      source:
        { mode: 'from_asr_source' } | { mode: 'analysis_only'; transcriptId: string } = {
        mode: 'from_asr_source',
      }
    ) => {
      if (isPolling) {
        logWarning('Reanalyze already in progress', { component: 'useReanalyzeStatus' });
        return;
      }

      try {
        // Reset state
        setStatus('queued');
        setError(null);
        setElapsedSeconds(0);
        setIsPolling(true);
        recordIdRef.current = recordId;
        expectedCurrentAnalysisIdRef.current = expectedCurrentAnalysisId;
        replayBaselineRef.current = null;
        startTimeRef.current = Date.now();

        logInfo('Starting reanalyze', {
          component: 'useReanalyzeStatus',
          recordId,
          promptProfileId,
        });

        // Call re-analyze API
        await apiClient.records.reanalyzeRecord(recordId, {
          promptProfileId: promptProfileId || undefined,
          mode: source.mode,
          publish: 'promote',
          deliverToCrm: false,
          ...(source.mode === 'analysis_only'
            ? { transcriptId: source.transcriptId }
            : {}),
          ...(expectedCurrentAnalysisId !== undefined
            ? { expectedCurrentAnalysisId }
            : {}),
        });

        // Start elapsed time counter
        elapsedIntervalRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setElapsedSeconds(elapsed);
        }, 1000);

        // Start polling
        pollIntervalRef.current = setInterval(pollStatus, pollIntervalMs);

        // Set timeout
        timeoutRef.current = setTimeout(() => {
          cleanup();
          setStatus('timeout');
          setError('Analysis timed out after 2 minutes');
          onTimeout?.();
          logWarning('Reanalyze timed out', {
            component: 'useReanalyzeStatus',
            recordId,
            timeoutMs,
          });
        }, timeoutMs);

        // Do initial poll
        await pollStatus();
      } catch (err) {
        cleanup();
        setStatus('failed');
        if (onProcessingPaused && isProcessingPausedError(err)) {
          logInfo('Reanalyze refused: processing of the source is paused', {
            component: 'useReanalyzeStatus',
            recordId,
          });
          onProcessingPaused();
          return;
        }
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to start re-analysis';
        setError(errorMsg);
        onError?.(errorMsg);
        logError(
          'Failed to start reanalyze',
          { recordId, promptProfileId },
          err instanceof Error ? err : undefined
        );
      }
    },
    [
      isPolling,
      apiClient,
      pollIntervalMs,
      timeoutMs,
      pollStatus,
      cleanup,
      onError,
      onProcessingPaused,
      onTimeout,
    ]
  );

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    status,
    error,
    isPolling,
    startReanalyze,
    cancel,
    elapsedSeconds,
  };
}
